/**
 * UC-197 / UC-198 shared report projection for Sold as / Bought as.
 * Wraps Inc-17 effectiveColumn — do not fork a second resolver.
 */
import {
  effectiveColumn,
  isBlankValue,
  type DesignationDefaults,
  type DesignationRole,
  type DesignationVariantRow,
  type IdentifierRef,
} from '@/lib/designations/organisationDefaults';
import {
  designationValuesFromIdentifierSources,
  type DesignationMatrixOptions,
} from '@/shared/variantMapping/designationValues';
import { toMatchKey } from '@/shared/variantMapping/matchKey';

export const SOLD_AS_COLUMN = 'Sold as';
export const BOUGHT_AS_COLUMN = 'Bought as';

/**
 * Exact identity key, plus loose keys used only when they name one master.
 * A case-folded key owned by more than one master is ambiguous: the identifier
 * is still mapped, but Sold as / Bought as are not guessed.
 */
export type IdentifierMasterIndex = {
  exact: Map<string, string>;
  spacing: Map<string, string>;
  caseFold: Map<string, string>;
  ambiguousCaseFold: ReadonlySet<string>;
  mastersWithValues: ReadonlySet<string>;
};

function spacingCollapsedKey(value: string): string {
  return toMatchKey(value).replace(/\s+/g, '');
}

function caseFoldKey(value: string): string {
  return toMatchKey(value).toLowerCase();
}

export function identifierValueToMasterId(
  valuesByMaster: Map<string, Record<string, string | null>>,
): IdentifierMasterIndex {
  const exact = new Map<string, string>();
  const spacingOwners = new Map<string, Set<string>>();
  const caseOwners = new Map<string, Set<string>>();
  const mastersWithValues = new Set<string>();

  for (const [masterId, values] of valuesByMaster) {
    for (const value of Object.values(values)) {
      if (typeof value !== 'string') continue;
      const key = toMatchKey(value);
      if (!key) continue;
      mastersWithValues.add(masterId);
      if (!exact.has(key)) exact.set(key, masterId);
      const collapsed = spacingCollapsedKey(key);
      if (collapsed) {
        const owners = spacingOwners.get(collapsed) ?? new Set<string>();
        owners.add(masterId);
        spacingOwners.set(collapsed, owners);
      }
      const folded = caseFoldKey(key);
      if (folded) {
        const owners = caseOwners.get(folded) ?? new Set<string>();
        owners.add(masterId);
        caseOwners.set(folded, owners);
      }
    }
  }

  const spacing = new Map<string, string>();
  for (const [collapsed, owners] of spacingOwners) {
    if (owners.size !== 1) continue;
    const masterId = owners.values().next().value;
    if (masterId) spacing.set(collapsed, masterId);
  }

  const caseFold = new Map<string, string>();
  const ambiguousCaseFold = new Set<string>();
  for (const [folded, owners] of caseOwners) {
    if (owners.size !== 1) {
      ambiguousCaseFold.add(folded);
      continue;
    }
    const masterId = owners.values().next().value;
    if (masterId) caseFold.set(folded, masterId);
  }

  return { exact, spacing, caseFold, ambiguousCaseFold, mastersWithValues };
}

function reportRowIdentifierCandidates(row: Record<string, unknown>): string[] {
  const candidates: string[] = [];
  for (const value of [row.Product, row.SKU]) {
    if (typeof value === 'string' && value.trim()) candidates.push(toMatchKey(value));
  }
  let constituents: unknown = row.constituent_skus;
  if (typeof constituents === 'string') {
    try { constituents = JSON.parse(constituents); } catch { constituents = null; }
  }
  if (!Array.isArray(constituents)) return candidates;
  for (const item of constituents) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    for (const key of ['identifier_value', 'seller_sku', 'value', 'asin', 'barcode']) {
      const value = rec[key];
      if (typeof value === 'string' && value.trim()) candidates.push(toMatchKey(value));
    }
  }
  return candidates;
}

function resolveIndexedMaster(candidate: string, index: IdentifierMasterIndex): string {
  const exact = index.exact.get(candidate);
  if (exact) return exact;
  const collapsed = spacingCollapsedKey(candidate);
  if (collapsed) {
    const spaced = index.spacing.get(collapsed);
    if (spaced) return spaced;
  }
  const folded = caseFoldKey(candidate);
  if (!folded) return '';
  return index.caseFold.get(folded) ?? '';
}

/** True when Product/SKU is an identifier on a visible variant, even if Sold as / Bought as are blank. */
export function reportRowIdentifierIsMapped(
  row: Record<string, unknown>,
  identifierToMaster?: IdentifierMasterIndex,
): boolean {
  if (!identifierToMaster) return false;
  if (reportRowMasterProductId(row, identifierToMaster)) return true;
  for (const candidate of reportRowIdentifierCandidates(row)) {
    const folded = caseFoldKey(candidate);
    if (folded && identifierToMaster.ambiguousCaseFold.has(folded)) return true;
  }
  return false;
}

/** Prefer a stamped master that has designation values; otherwise match Product/SKU/constituents. */
export function reportRowMasterProductId(
  row: Record<string, unknown>,
  identifierToMaster?: IdentifierMasterIndex,
): string {
  const stamped =
    (typeof row.master_product_id === 'string' && row.master_product_id.trim())
    || (typeof row['Master Product Id'] === 'string' && row['Master Product Id'].trim())
    || '';
  if (stamped && (!identifierToMaster || identifierToMaster.mastersWithValues.has(stamped))) {
    return stamped;
  }
  if (!identifierToMaster) return '';
  for (const candidate of reportRowIdentifierCandidates(row)) {
    const masterId = resolveIndexedMaster(candidate, identifierToMaster);
    if (masterId) return masterId;
  }
  return '';
}

export type ReportDesignationOverride = {
  master_product_id: string;
  sold_as_column_key: string | null;
  sold_as_identifier_type: string | null;
  bought_as_column_key: string | null;
  bought_as_identifier_type: string | null;
};

/** Stored keys keep the identifier type as saved. Matrix keys are lowercase. */
function foldDesignationColumnKey(columnKey: string): string {
  const separator = columnKey.indexOf(':');
  if (separator <= 0) return columnKey.trim().toLowerCase();
  return `${columnKey.slice(0, separator).trim().toLowerCase()}${columnKey.slice(separator)}`;
}

function rawDesignationValue(
  values: Record<string, string | null | undefined>,
  columnKey: string,
): string | null | undefined {
  if (Object.prototype.hasOwnProperty.call(values, columnKey)) return values[columnKey];
  const folded = foldDesignationColumnKey(columnKey);
  if (folded !== columnKey && Object.prototype.hasOwnProperty.call(values, folded)) return values[folded];
  return undefined;
}

/** Display value for one designation role — empty string when missing (never a fallback). */
export function reportEffectiveDesignationValue(
  row: DesignationVariantRow,
  role: DesignationRole,
  defaults: DesignationDefaults,
): string {
  const column = effectiveColumn(row, role, defaults);
  if (column == null) return '';
  const raw = rawDesignationValue(row.valuesByColumn, column.columnKey);
  if (isBlankValue(raw)) return '';
  return String(raw).trim();
}

export function reportEffectiveDesignations(
  row: DesignationVariantRow,
  defaults: DesignationDefaults,
): { soldAs: string; boughtAs: string } {
  return {
    soldAs: reportEffectiveDesignationValue(row, 'sold_as', defaults),
    boughtAs: reportEffectiveDesignationValue(row, 'bought_as', defaults),
  };
}

function refFromOverride(
  columnKey: string | null | undefined,
  identifierType: string | null | undefined,
): IdentifierRef | null {
  if (!columnKey || !identifierType) return null;
  return { columnKey, identifierType };
}

export type ReportIdentifierLink = {
  master_product_id?: string | null;
  identifier_type?: string | null;
  identifier_value?: string | null;
  type?: string | null;
  value?: string | null;
  active?: boolean | null;
  created_at?: string | null;
};

export type ReportIdentifierObservation = {
  duplicate_of?: string | null;
  interpreted_identifier_type?: string | null;
  raw_source_label?: string | null;
  raw_as_observed_value?: string | null;
};

/**
 * Column keys follow the mapping matrix `{type}:{occurrence}` (e.g. sku:0).
 * Occurrence matches Variant Mapping: matched observations in input order, then
 * active links that are not already in that list. A missing key stays absent.
 */
export function valuesByColumnFromIdentifierLinks(
  links: ReadonlyArray<ReportIdentifierLink>,
): Record<string, string | null> {
  const values: Record<string, string | null> = {};
  const typeCounts: Record<string, number> = {};
  const seen = new Set<string>();

  for (const link of links) {
    if (link.active === false) continue;
    const trimmedType = (link.identifier_type ?? link.type)?.trim().toLowerCase() ?? '';
    const trimmedValue = (link.identifier_value ?? link.value)?.trim() ?? '';
    if (!trimmedType || !trimmedValue) continue;
    const identity = `${trimmedType}\0${trimmedValue}`;
    if (seen.has(identity)) continue;
    seen.add(identity);
    const occurrence = typeCounts[trimmedType] ?? 0;
    typeCounts[trimmedType] = occurrence + 1;
    values[`${trimmedType}:${occurrence}`] = trimmedValue;
  }

  return values;
}

/**
 * Same occurrence order as the Variant Mapping screen: matched observations
 * first (caller order), then active links that are not already present.
 */
export function valuesByColumnByMasterProduct(
  links: ReadonlyArray<ReportIdentifierLink>,
  observations: ReadonlyArray<ReportIdentifierObservation> = [],
  options?: DesignationMatrixOptions,
): Map<string, Record<string, string | null>> {
  return designationValuesFromIdentifierSources({
    links,
    observations,
    masters: options?.masters,
    cloneReadFilter: options?.cloneReadFilter,
  });
}

/** Trailing 12 months is 365 days. Any other window uses inclusive start and end dates. */
export function reportDaysInPeriod(filters?: {
  trailing12Months?: boolean;
  startDate?: string | null;
  endDate?: string | null;
}): number {
  if (filters?.trailing12Months) return 365;
  if (!filters?.startDate || !filters?.endDate) return 365;
  const start = new Date(filters.startDate);
  const end = new Date(filters.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 365;
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

export function designationRowFromReportRow(
  row: Record<string, unknown>,
  override: ReportDesignationOverride | undefined,
  valuesByColumn: Record<string, string | null> = {},
): DesignationVariantRow {
  const masterProductId =
    (typeof row.master_product_id === 'string' && row.master_product_id) ||
    (typeof row['Master Product Id'] === 'string' && row['Master Product Id']) ||
    '';
  return {
    masterProductId,
    eveId: 0,
    soldAsOverride: refFromOverride(override?.sold_as_column_key, override?.sold_as_identifier_type),
    boughtAsOverride: refFromOverride(override?.bought_as_column_key, override?.bought_as_identifier_type),
    valuesByColumn,
  };
}

/** Identifier printed on the report row itself (SKU, otherwise Product). */
export function reportRowVariantLabel(row: Record<string, unknown>): string {
  for (const value of [row.SKU, row.Product]) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function hasMappedDesignationValue(
  values: Record<string, string | null | undefined>,
): boolean {
  return Object.values(values).some((value) => typeof value === 'string' && value.trim() !== '');
}

/**
 * Sold as and Bought as show the report identifier only when that value is not
 * in Variant Mapping. A mapped variant whose Sold as / Bought as are unassigned
 * stays blank.
 */
export function withUnmappedVariantLabel(
  row: Record<string, unknown>,
  soldAs: string,
  boughtAs: string,
  valuesByColumn: Record<string, string | null | undefined>,
): { soldAs: string; boughtAs: string } {
  if (soldAs || boughtAs || hasMappedDesignationValue(valuesByColumn)) {
    return { soldAs, boughtAs };
  }
  const label = reportRowVariantLabel(row);
  if (!label) return { soldAs, boughtAs };
  return { soldAs: label, boughtAs: label };
}

export function projectSoldAsBoughtAsOntoReportRow(
  row: Record<string, unknown>,
  defaults: DesignationDefaults,
  override: ReportDesignationOverride | undefined,
  valuesByColumn: Record<string, string | null> = {},
  options?: { applyUnmappedIdentifier?: boolean },
): Record<string, unknown> {
  const designationRow = designationRowFromReportRow(row, override, valuesByColumn);
  const resolved = reportEffectiveDesignations(designationRow, defaults);
  const { soldAs, boughtAs } = options?.applyUnmappedIdentifier === false
    ? resolved
    : withUnmappedVariantLabel(row, resolved.soldAs, resolved.boughtAs, valuesByColumn);
  const { Product: _product, SKU: _sku, ...rest } = row;
  return {
    ...rest,
    [BOUGHT_AS_COLUMN]: boughtAs,
    [SOLD_AS_COLUMN]: soldAs,
    // Keep originals for expansion fallback / internal use — not shown as columns
    Product: row.Product,
    SKU: row.SKU,
  };
}

/** Replace Product/SKU identifier columns with Bought as then Sold as (adjacent). */
export function replaceProductColumnsWithDesignations(columns: string[]): string[] {
  const withoutProduct = columns.filter((col) => col !== 'Product' && col !== 'SKU');
  const insertAt = (() => {
    const productIdx = columns.findIndex((col) => col === 'Product' || col === 'SKU');
    return productIdx === -1 ? 0 : productIdx;
  })();
  const next = [...withoutProduct];
  next.splice(insertAt, 0, BOUGHT_AS_COLUMN, SOLD_AS_COLUMN);
  return next;
}

export function matchesDesignationFilter(
  cellValue: string,
  filterValue: string,
  operation: string,
): boolean {
  const cell = cellValue ?? '';
  const target = filterValue ?? '';
  switch (operation) {
    case 'equals':
      return cell.toLowerCase() === target.toLowerCase();
    case 'not_equals':
      return cell.toLowerCase() !== target.toLowerCase();
    case 'contains':
    case 'like':
      return cell.toLowerCase().includes(target.toLowerCase());
    case 'not_like':
      return !cell.toLowerCase().includes(target.toLowerCase());
    case 'startsWith':
      return cell.toLowerCase().startsWith(target.toLowerCase());
    case 'endsWith':
      return cell.toLowerCase().endsWith(target.toLowerCase());
    case 'is_blank':
      return cell.trim() === '';
    case 'is_not_blank':
      return cell.trim() !== '';
    default:
      return false;
  }
}

export function isDesignationFilterColumn(column: string | undefined): boolean {
  return column === SOLD_AS_COLUMN || column === BOUGHT_AS_COLUMN;
}

/** Sold as / Bought as are not SQL columns. Keep them out of the report query. */
export function columnFiltersForReportQuery<T extends { column?: string }>(filters: readonly T[]): T[] {
  return filters.filter((filter) => !isDesignationFilterColumn(filter.column));
}

export function paginateRows<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  const size = pageSize > 0 ? pageSize : rows.length || 0;
  const current = page > 0 ? page : 1;
  const start = (current - 1) * size;
  return rows.slice(start, start + size);
}

const SUM_TOTAL_FIELDS: Array<[string, string]> = [
  ['∑: GMV $', 'Total GMV $'],
  ['∑: NMV $', 'Total NMV $'],
  ['Fee Allocation $', 'Total Fee Allocation $'],
  ['∑: Units Sold', 'Total Units Sold'],
  ['Stock Held', 'Total Stock Held'],
  ['∑: GEV $', 'Total GEV $'],
  ['∑: NEV $', 'Total NEV $'],
  ['∑: NERV $', '∑: NERV $'],
  ['NERV (in 1 month)', 'NERV (in 1 month)'],
  ['NERV (in 3 months)', 'NERV (in 3 months)'],
  ['NERV (in 6 months)', 'NERV (in 6 months)'],
];

function parseReportNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = parseFloat(String(value).replace(/[$,%\s]/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Footer totals for a designation-filtered set.
 * AVG GMV and AVG NMV are total sales divided by total units, matching the unfiltered report.
 * Days until stockout uses aggregate stock and units over the trailing period (365 days).
 * DR: SKU % is the org rate carried on each row, not an average of rates.
 */
export function totalsFromFilteredReportRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  daysInPeriod = 365,
): Record<string, number | null> {
  const totals: Record<string, number | null> = {};
  for (const [column, field] of SUM_TOTAL_FIELDS) {
    let sum = 0;
    for (const row of rows) {
      const parsed = parseReportNumber(row[column]);
      if (parsed != null) sum += parsed;
    }
    totals[field] = sum;
  }

  const totalUnits = totals['Total Units Sold'] ?? 0;
  const totalStock = totals['Total Stock Held'] ?? 0;
  totals['AVG GMV $'] = totalUnits > 0 ? (totals['Total GMV $'] ?? 0) / totalUnits : 0;
  totals['AVG NMV $'] = totalUnits > 0 ? (totals['Total NMV $'] ?? 0) / totalUnits : 0;
  // No units sold stays empty so the table shows "No sales", including when stock is held.
  totals['Days Until Stockout'] = totalUnits > 0 ? (totalStock * daysInPeriod) / totalUnits : null;

  const drPercent = rows
    .map((row) => parseReportNumber(row['DR: SKU %']))
    .find((value) => value != null);
  totals['DR: SKU %'] = drPercent ?? 0;
  return totals;
}

export function filterRowsByDesignation(
  rows: Record<string, unknown>[],
  filters: Array<{ column: string; value: string; operation: string }>,
): Record<string, unknown>[] {
  const designationFilters = filters.filter(
    (f) => f.column === SOLD_AS_COLUMN || f.column === BOUGHT_AS_COLUMN,
  );
  if (designationFilters.length === 0) return rows;
  return rows.filter((row) =>
    designationFilters.every((f) =>
      matchesDesignationFilter(String(row[f.column] ?? ''), f.value, f.operation),
    ),
  );
}
